/**
 * Setup "Condition turns true": 1–3 indicator / market-state conditions as the trigger.
 * The scan fires on the bar where every condition becomes true together.
 * Picks go to `setup.params.conditions` as "rsi:overbought" / "ema:50" / "killzone".
 */

import type { StrategyRdComposerSetupSlot } from "../types/strategyRd";

export const STRATEGY_RD_CONDITION_SETUP_ID = "condition";
export const STRATEGY_RD_CONDITION_MAX_DEFAULT = 3;

/** One row of GET /setups/condition-choices: a family with labels / lengths, or a plain concept. */
export type StrategyRdConditionChoice = {
  id: string;
  name: string;
  labels?: string[];
  lengths?: number[];
};

export type StrategyRdConditionChoices = {
  maxConditions: number;
  choices: StrategyRdConditionChoice[];
};

export type StrategyRdConditionOptionGroup = {
  id: string;
  label: string;
  options: { value: string; label: string }[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function parseStrategyRdConditionChoices(raw: unknown): StrategyRdConditionChoices {
  const r = asRecord(raw) || {};
  const max = Number(r.max_conditions);
  const choices: StrategyRdConditionChoice[] = [];
  for (const item of Array.isArray(r.choices) ? r.choices : []) {
    const rec = asRecord(item);
    const id = typeof rec?.id === "string" ? rec.id.trim().toLowerCase() : "";
    if (!rec || !id) continue;
    const labels = Array.isArray(rec.labels)
      ? rec.labels.filter((x): x is string => typeof x === "string" && x.trim() !== "")
      : [];
    const lengths = Array.isArray(rec.lengths)
      ? rec.lengths.map(Number).filter((n) => Number.isFinite(n) && n > 0)
      : [];
    choices.push({
      id,
      name: typeof rec.name === "string" && rec.name.trim() ? rec.name.trim() : id.replace(/_/g, " "),
      ...(labels.length ? { labels } : {}),
      ...(lengths.length ? { lengths } : {}),
    });
  }
  return {
    maxConditions: Number.isFinite(max) && max >= 1 ? Math.floor(max) : STRATEGY_RD_CONDITION_MAX_DEFAULT,
    choices,
  };
}

export function strategyRdIsConditionSetup(setupId: string | null | undefined): boolean {
  return String(setupId || "").trim().toLowerCase() === STRATEGY_RD_CONDITION_SETUP_ID;
}

/** The picks on a setup slot ("rsi:overbought", …), in order. */
export function strategyRdSetupConditions(
  setup: Pick<StrategyRdComposerSetupSlot, "params"> | null | undefined
): string[] {
  const raw = setup?.params?.conditions;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => (typeof x === "string" ? x.trim().toLowerCase() : ""))
    .filter(Boolean);
}

function humanize(text: string): string {
  const t = text.replace(/_/g, " ").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

function pickFamily(pick: string): string {
  return pick.split(":", 1)[0];
}

/** "RSI · Overbought", "EMA 50", "Killzone" — family names from the choices when loaded. */
export function strategyRdConditionPickLabel(
  pick: string,
  choices?: readonly StrategyRdConditionChoice[] | null
): string {
  const [family, arg] = pick.includes(":") ? pick.split(/:(.*)/s, 2) : [pick, ""];
  const name = choices?.find((c) => c.id === family)?.name || humanize(family);
  if (!arg) return name;
  return /^\d+$/.test(arg) ? `${name} ${arg}` : `${name} · ${humanize(arg)}`;
}

/** Picker groups: one per family (its labels / lengths), then the plain conditions together. */
export function strategyRdConditionOptionGroups(
  choices: readonly StrategyRdConditionChoice[]
): StrategyRdConditionOptionGroup[] {
  const groups: StrategyRdConditionOptionGroup[] = [];
  const plain: { value: string; label: string }[] = [];
  for (const c of choices) {
    if (c.labels?.length) {
      groups.push({
        id: c.id,
        label: c.name,
        options: c.labels.map((lab) => ({ value: `${c.id}:${lab}`, label: `${c.name} · ${humanize(lab)}` })),
      });
    } else if (c.lengths?.length) {
      groups.push({
        id: c.id,
        label: c.name,
        options: c.lengths.map((n) => ({ value: `${c.id}:${n}`, label: `${c.name} ${n}` })),
      });
    } else {
      plain.push({ value: c.id, label: c.name });
    }
  }
  if (plain.length) groups.push({ id: "other", label: "Other conditions", options: plain });
  return groups;
}

/**
 * One pick per family (the newest replaces "RSI · Oversold" with "RSI · Overbought"), and no
 * more than `max` — an extra pick past the cap is ignored.
 */
export function strategyRdNormalizeConditionPicks(
  next: readonly string[],
  previous: readonly string[],
  max: number = STRATEGY_RD_CONDITION_MAX_DEFAULT
): string[] {
  const added = next.filter((p) => !previous.includes(p));
  let out = next.filter((p) => !added.includes(p));
  for (const pick of added) {
    const fam = pickFamily(pick);
    const at = out.findIndex((p) => pickFamily(p) === fam);
    if (at >= 0) out = out.map((p, i) => (i === at ? pick : p));
    else if (out.length < max) out = [...out, pick];
  }
  return out;
}

/**
 * Catalog settings rebuild `params` when the side changes; keep the picks while the setup
 * stays "condition".
 */
export function strategyRdKeepSetupConditions(
  previous: StrategyRdComposerSetupSlot,
  next: StrategyRdComposerSetupSlot
): StrategyRdComposerSetupSlot {
  if (!strategyRdIsConditionSetup(next.id) || !strategyRdIsConditionSetup(previous.id)) return next;
  if (strategyRdSetupConditions(next).length > 0) return next;
  const kept = strategyRdSetupConditions(previous);
  if (kept.length === 0) return next;
  return { ...next, params: { ...(next.params || {}), conditions: kept } };
}

export function strategyRdWithSetupConditions(
  setup: StrategyRdComposerSetupSlot,
  conditions: readonly string[]
): StrategyRdComposerSetupSlot {
  return { ...setup, params: { ...(setup.params || {}), conditions: [...conditions] } };
}

/** Both ways: the other side trades the opposite conditions (overbought ↔ oversold…). */
export function strategyRdSetupMirror(setup: StrategyRdComposerSetupSlot): boolean {
  return Boolean((setup.params as Record<string, unknown> | null | undefined)?.mirror);
}

/**
 * Turn "both ways" on or off. On: the conditions as picked are what the SHORT trades (overbought
 * is a short's condition), the long trades their opposites, and Trade side becomes both.
 */
export function strategyRdWithSetupMirror(
  setup: StrategyRdComposerSetupSlot,
  mirror: boolean
): StrategyRdComposerSetupSlot {
  const params: Record<string, unknown> = { ...(setup.params || {}) };
  if (mirror) {
    params.mirror = true;
    return { ...setup, side: "both", params } as StrategyRdComposerSetupSlot;
  }
  delete params.mirror;
  delete params.primary_side;
  return { ...setup, params } as StrategyRdComposerSetupSlot;
}

/** Why Run backtest can't start for this setup, or null. */
export function strategyRdConditionSetupBlockingReason(
  setup: StrategyRdComposerSetupSlot,
  max: number = STRATEGY_RD_CONDITION_MAX_DEFAULT
): string | null {
  if (!strategyRdIsConditionSetup(setup.id)) return null;
  const n = strategyRdSetupConditions(setup).length;
  if (n === 0) return `Pick 1 to ${max} conditions for the setup (e.g. RSI overbought).`;
  if (n > max) return `At most ${max} conditions in the setup; add others as confluences.`;
  return null;
}
